import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import { fm } from '../utils';
import ReportsDashboard from '../components/reports/ReportsDashboard';
import DashboardMain from '../components/dashboard/DashboardMain';
import SiteSettings from '../components/site-settings/SiteSettings';
import { 
  LayoutDashboard, Package, ListTree, Users, 
  Settings, LogOut, Search, Plus, Trash2, 
  Edit2, Filter, MoreVertical, ChevronRight,
  TrendingUp, TrendingDown, Clock, CheckCircle2,
  AlertCircle, DollarSign, Image as ImageIcon,
  MessageSquare, Calendar, Globe, Printer
} from 'lucide-react';
import YaziciYonetimi from '../components/dashboard/YaziciYonetimi';
import ModifierYonetimi from '../components/modifier/ModifierYonetimi';
import './AdminDashboard.css';

const API = process.env.REACT_APP_API_URL || '/api';

const TabButton = ({ active, onClick, icon, label, count }) => (
  <button onClick={onClick}
    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg whitespace-nowrap transition-all duration-200 ${
      active
        ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25 scale-[1.02]'
        : 'text-surface-700 hover:text-surface-900 hover:bg-surface-100 active:scale-[0.97]'
    }`}>
    <span className="text-[13px] leading-none">{icon}</span>
    <span>{label}</span>
    {count !== undefined && (
      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
        active ? 'bg-white/20 text-white' : 'bg-surface-100 text-surface-500'
      }`}>
        {count}
      </span>
    )}
  </button>
);

const CrudTable = ({ type, list, cols, labels, idKey, onEdit, onAdd, onDel }) => (
  <div className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft overflow-hidden">
    <div className="flex items-center justify-between p-4 border-b border-surface-100 dark:border-surface-700">
      <h3 className="font-display font-bold text-surface-800 dark:text-white text-sm capitalize">{type}</h3>
      <button onClick={onAdd}
        className="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold rounded-xl shadow-lg shadow-emerald-500/20 transition-all">
        + Yeni Ekle
      </button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-surface-50 dark:bg-surface-700/30">
            {labels.map((l, i) => <th key={i} className="text-left px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">{l}</th>)}
            <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">İşlem</th>
          </tr>
        </thead>
        <tbody>
          {list.map(item => (
            <tr key={item[idKey]} className="border-t border-surface-100 dark:border-surface-700/50 hover:bg-surface-50 dark:hover:bg-surface-700/20 transition-colors">
              {cols.map((c, i) => (
                <td key={i} className="px-4 py-2.5 text-surface-600 dark:text-surface-300">
                  {typeof item[c] === 'number' && c.includes('fiyat') ? `${fm(item[c])}` :
                   c === 'aktif' ?
                   <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${item[c] !== false ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300'}`}>{item[c] !== false ? 'Aktif' : 'Pasif'}</span> :
                   item[c]}
                </td>
              ))}
              <td className="px-4 py-2.5 text-right">
                <div className="flex items-center justify-end gap-1.5">
                  <button onClick={() => onEdit(item)}
                    className="px-2.5 py-1 text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">
                    Düzenle
                  </button>
                  <button onClick={() => onDel(item[idKey])}
                    className="px-2.5 py-1 text-[10px] font-semibold bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">
                    Sil
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const formatSaat = (tarih) => {
  if (!tarih) return '--:--';
  return new Date(tarih).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const sureFarki = (tarih) => {
  if (!tarih) return null;
  const sn = Math.floor((Date.now() - new Date(tarih).getTime()) / 1000);
  if (sn < 0) return '00:00';
  const dk = Math.floor(sn / 60);
  const ss = Math.floor(dk / 60);
  return `${String(ss).padStart(2, '0')}:${String(dk % 60).padStart(2, '0')}`;
};

const sureDk = (tarih) => {
  if (!tarih) return 999;
  return Math.floor((Date.now() - new Date(tarih).getTime()) / 60000);
};

const kartRenk = (sonTarih) => {
  const dk = sureDk(sonTarih);
  if (dk < 15) return {
    accent: 'border-l-emerald-500',
    bg: 'bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/15 dark:to-surface-800',
    border: 'border-emerald-200/50 dark:border-emerald-800/30',
    badge: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
    dot: 'bg-emerald-500',
    text: 'text-emerald-600 dark:text-emerald-400',
    shadow: 'shadow-emerald-500/5',
    pulse: false
  };
  if (dk < 30) return {
    accent: 'border-l-amber-500',
    bg: 'bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/15 dark:to-surface-800',
    border: 'border-amber-200/50 dark:border-amber-800/30',
    badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-500',
    text: 'text-amber-600 dark:text-amber-400',
    shadow: 'shadow-amber-500/5',
    pulse: false
  };
  return {
    accent: 'border-l-red-500',
    bg: 'bg-gradient-to-br from-red-50 to-white dark:from-red-900/15 dark:to-surface-800',
    border: 'border-red-200/50 dark:border-red-800/30',
    badge: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    dot: 'bg-red-500',
    text: 'text-red-600 dark:text-red-400',
    shadow: 'shadow-red-500/5',
    pulse: true
  };
};

const SiparisTab = () => {
  const { token } = useAuth();
  const h = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [kartlar, setKartlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seciliDetay, setSeciliDetay] = useState(null);
  const [detayYukleniyor, setDetayYukleniyor] = useState(false);

  const fetchKartlar = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const r = await fetch(`${API}/siparisler/masa-kartlari`, { headers: h });
      if (r.ok) setKartlar(await r.json());
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  };

  useEffect(() => {
    fetchKartlar();
    const iv = setInterval(() => fetchKartlar(true), 30000);
    return () => clearInterval(iv);
  }, []);

  const detayAc = async (masaKart) => {
    setDetayYukleniyor(true);
    try {
      const r = await fetch(`${API}/siparisler/${masaKart.sonSiparisID}`, { headers: h });
      if (r.ok) setSeciliDetay(await r.json());
    } catch (e) { console.error(e); }
    finally { setDetayYukleniyor(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (kartlar.length === 0) return (
    <div className="text-center py-16 text-surface-400 text-sm">Aktif sipariş bulunmuyor</div>
  );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {kartlar.map(k => {
          const renk = kartRenk(k.sonSiparisTarihi);
          const sure = sureFarki(k.sonSiparisTarihi);
          return (
            <div key={k.masaID} className={`relative rounded-2xl border ${renk.border} ${renk.bg} ${renk.shadow} shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden group ${renk.pulse ? 'animate-pulse-soft' : ''}`}>
              <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${renk.accent} rounded-l-2xl`} />
              <div className="p-4 pl-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-surface-800 dark:text-white text-lg">Masa {k.masaNumarasi}</h3>
                    {k.konum && <p className="text-[11px] text-surface-400 flex items-center gap-1 mt-0.5">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      {k.konum}
                    </p>}
                  </div>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tabular-nums ${renk.badge} ${renk.pulse ? 'animate-pulse' : ''}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${renk.dot}`} />
                    {sure}
                  </div>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-surface-400 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                      Garson
                    </span>
                    <span className="font-semibold text-surface-700 dark:text-surface-200">{k.garsonAdi || '?'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-surface-400">
                      <svg className="w-3 h-3 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      İlk Sipariş
                    </span>
                    <span className="font-medium text-surface-700 dark:text-surface-200">{formatSaat(k.ilkSiparisTarihi)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-surface-400">
                      <svg className="w-3 h-3 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Son Sipariş
                    </span>
                    <span className="font-medium text-surface-700 dark:text-surface-200">{formatSaat(k.sonSiparisTarihi)}</span>
                  </div>

                  <div className="border-t border-surface-100 dark:border-surface-700/50 my-2" />

                  <div className="flex items-center justify-between">
                    <span className="text-surface-400">
                      <svg className="w-3 h-3 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                      Kalem
                    </span>
                    <span className="font-semibold text-surface-800 dark:text-white">{k.toplamKalem} ürün</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-surface-400">
                      <svg className="w-3 h-3 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Tutar
                    </span>
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">{fm(k.toplamTutar)}</span>
                  </div>
                </div>
              </div>

              <div className="px-4 pb-4 pl-5">
                <button onClick={() => detayAc(k)}
                  className="w-full py-2.5 text-xs font-semibold bg-white/80 dark:bg-surface-700/80 backdrop-blur-sm border border-surface-200 dark:border-surface-600 text-surface-600 dark:text-surface-300 rounded-xl hover:bg-white dark:hover:bg-surface-600 hover:border-surface-300 dark:hover:border-surface-500 transition-all active:scale-[0.97] opacity-80 group-hover:opacity-100">
                  <svg className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                  Sipariş Detayı
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {seciliDetay && (
        <SiparisDetayModal
          detay={seciliDetay}
          yukleniyor={detayYukleniyor}
          onClose={() => setSeciliDetay(null)}
          onAdetGuncelle={async (detayId, yeniAdet) => {
            try {
              const res = await fetch(`${API}/siparisler/detay/${detayId}/adet`, { method: 'PATCH', headers: h, body: JSON.stringify(yeniAdet) });
              if (res.ok) { detayAc({ sonSiparisID: seciliDetay.siparisID }); fetchKartlar(); }
            } catch (e) { console.error(e); }
          }}
          onIptalEt={async (detayId) => {
            const result = await Swal.fire({ title: 'Emin misiniz?', text: 'Bu ürünü iptal etmek istediğinize emin misiniz?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Evet, İptal Et', cancelButtonText: 'Hayır', confirmButtonColor: '#ef4444' });
            if (!result.isConfirmed) return;
            try {
              const res = await fetch(`${API}/siparisler/detay/${detayId}/durum`, { method: 'PATCH', headers: h, body: JSON.stringify('İptal') });
              if (res.ok) { detayAc({ sonSiparisID: seciliDetay.siparisID }); fetchKartlar(); Swal.fire('İptal Edildi!', '', 'success'); }
            } catch (e) { console.error(e); }
          }}
        />
      )}
    </>
  );
};

const SiparisDetayModal = ({ detay, yukleniyor, onClose, onAdetGuncelle, onIptalEt }) => {
  if (yukleniyor) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white dark:bg-surface-800 rounded-2xl p-8 shadow-2xl">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    </div>
  );
  if (!detay) return null;
  const sip = detay;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-white dark:bg-surface-800 rounded-2xl shadow-2xl animate-scale-up max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-surface-100 dark:border-surface-700 sticky top-0 bg-white dark:bg-surface-800 z-10">
          <div>
            <h3 className="font-bold text-surface-800 dark:text-white">Masa {sip.masaNumarasi} - #{sip.siparisID}</h3>
            <p className="text-[11px] text-surface-400">{sip.garsonAdi} • {formatSaat(sip.siparisTarihi)}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-700 text-surface-400">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="p-4">
          {sip.siparisDetaylari && sip.siparisDetaylari.length > 0 ? (
            <div className="divide-y divide-surface-100 dark:divide-surface-700/50">
              {sip.siparisDetaylari.map((d, i) => (
                <div key={i} className={`flex items-center justify-between py-3 ${d.durum === 'İptal' ? 'opacity-60' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-semibold text-sm ${d.durum === 'İptal' ? 'text-red-400 line-through' : 'text-surface-800 dark:text-white'}`}>{d.urunAdi}</span>
                      {d.durum === 'İptal' && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-red-100 text-red-600">İptal</span>}
                    </div>
                    <p className="text-[11px] text-surface-400 mt-0.5">{d.adet} x {fm(d.birimFiyat || d.fiyat || 0)}</p>
                    {d.modifiers && d.modifiers.length > 0 && (() => {
                      const groups = {};
                      d.modifiers.forEach(m => {
                        const gName = m.modifierGroupAdi || 'Diğer';
                        if (!groups[gName]) groups[gName] = [];
                        const qty = m.qty || 1;
                        groups[gName].push(qty > 1 ? `${qty}x ${m.modifierItemAdi}` : m.modifierItemAdi);
                      });
                      return (
                        <div className="text-[10px] text-surface-400 mt-0.5 space-y-0.5">
                          {Object.entries(groups).map(([gAdi, items]) => (
                            <div key={gAdi} className="flex items-start gap-1">
                              <span className="font-medium text-surface-500 flex-shrink-0">{gAdi}:</span>
                              <span>{items.join(', ')}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    {d.notlar && <p className="text-[10px] text-amber-500 mt-0.5">📝 {d.notlar}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {d.durum !== 'İptal' && sip.odemeDurumu !== 'Ödendi' && sip.odemeDurumu !== 'Odendi' && (
                      <>
                        <div className="flex items-center gap-1 bg-surface-100 dark:bg-surface-700 rounded-lg p-0.5">
                          <button onClick={() => onAdetGuncelle(d.detayID, Math.max(1, d.adet - 1))}
                            className="w-6 h-6 rounded-md bg-white dark:bg-surface-600 flex items-center justify-center text-surface-500 hover:bg-surface-50 transition-colors">
                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                          </button>
                          <span className="w-5 text-center text-xs font-bold text-surface-700 dark:text-surface-300">{d.adet}</span>
                          <button onClick={() => onAdetGuncelle(d.detayID, d.adet + 1)}
                            className="w-6 h-6 rounded-md bg-white dark:bg-surface-600 flex items-center justify-center text-surface-500 hover:bg-surface-50 transition-colors">
                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                          </button>
                        </div>
                        <button onClick={() => onIptalEt(d.detayID)}
                          className="p-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">
                          <svg className="w-3.5 h-3.5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </>
                    )}
                    <span className="font-semibold text-surface-700 dark:text-surface-300 text-sm w-16 text-right">
                      {fm(d.toplamFiyat || (d.birimFiyat || d.fiyat || 0) * d.adet)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-surface-400 text-center py-4">Detay bulunamadı</p>}

          <div className="mt-4 pt-4 border-t border-surface-100 dark:border-surface-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-surface-500">Net Toplam</span>
              <span className="font-bold text-lg text-emerald-600 dark:text-emerald-400">{fm(sip.netTutar)}</span>
            </div>
            {sip.indirimTutari > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-red-500">İndirim</span>
                <span className="text-sm font-semibold text-red-500">-{fm(sip.indirimTutari)}</span>
              </div>
            )}
          </div>

          {sip.odemeler && sip.odemeler.length > 0 && (
            <div className="mt-4 pt-4 border-t border-surface-100 dark:border-surface-700">
              <p className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Ödemeler</p>
              {sip.odemeler.map(o => (
                <div key={o.odemeID} className="flex items-center justify-between text-xs py-1">
                  <span className="text-surface-500">{o.odemeTuru}</span>
                  <span className="font-semibold text-surface-700 dark:text-surface-300">{fm(o.toplamTutar)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StokTab = ({ hareketler, onAdd }) => (
  <div className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft overflow-hidden">
    <div className="flex items-center justify-between p-4 border-b border-surface-100 dark:border-surface-700">
      <h3 className="font-display font-bold text-surface-800 dark:text-white text-sm">Stok Hareketleri</h3>
      <button onClick={() => onAdd({ type: 'stok', item: null })}
        className="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold rounded-xl transition-all">
        + Stok Giriş/Çıkış
      </button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-surface-50 dark:bg-surface-700/30">
            {['Tarih', 'Ürün', 'Tür', 'Miktar', 'Açıklama'].map(h => (
              <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hareketler.map(h => (
            <tr key={h.hareketID} className="border-t border-surface-100 dark:border-surface-700/50 hover:bg-surface-50 dark:hover:bg-surface-700/20 transition-colors">
              <td className="px-4 py-2.5 text-surface-500">{new Date(h.tarih).toLocaleString('tr-TR')}</td>
              <td className="px-4 py-2.5 text-surface-600 dark:text-surface-300 font-medium">{h.urunAdi}</td>
              <td className="px-4 py-2.5">
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${h.hareketTuru === 'Giris' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300'}`}>
                  {h.hareketTuru}
                </span>
              </td>
              <td className="px-4 py-2.5 text-surface-600 dark:text-surface-300">{h.miktar}</td>
              <td className="px-4 py-2.5 text-surface-400">{h.aciklama || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const AdisyonTab = () => {
  const defaultSablon = `<div style="text-align:center;font-family:monospace;width:300px;margin:0 auto"><h2>NEŞVE CAFE</h2><p>Masa: {MASA_NO}</p><p>Tarih: {TARIH}</p><hr/><table style="width:100%">{URUNLER}</table><hr/><h3>Toplam: {TOPLAM} TL</h3><p>Afiyet olsun. Bizi tercih ettiğiniz için teşekkür ederiz.</p></div>`;
  const [sablon, setSablon] = useState(localStorage.getItem('adisyonSablonu') || defaultSablon);
  const save = () => {
    localStorage.setItem('adisyonSablonu', sablon);
    Swal.fire({ title: 'Başarılı!', text: 'Adisyon fiş şablonu kaydedildi!', icon: 'success', timer: 1500, showConfirmButton: false });
  };
  return (
    <div className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft p-6">
      <h3 className="font-display font-bold text-surface-800 dark:text-white text-sm mb-1">Adisyon Fişi Tasarımı</h3>
      <p className="text-[11px] text-surface-400 mb-4">Kullanılabilir değişkenler: <code className="text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded text-[10px]">{'{MASA_NO}, {TARIH}, {URUNLER}, {TOPLAM}'}</code></p>
      <textarea value={sablon} onChange={e => setSablon(e.target.value)}
        className="w-full h-64 font-mono text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-2xl p-4 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-y" />
      <button onClick={save}
        className="mt-4 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-xl shadow-lg shadow-emerald-500/20 transition-all">
        Tasarımı Kaydet
      </button>
    </div>
  );
};

const UrunlerTab = ({ kategoriler, urunler, onEdit, onAdd, onDel }) => {
  const [seciliKategori, setSeciliKategori] = useState(null);
  const aktifKategoriler = kategoriler.filter(k => k.aktif !== false);
  useEffect(() => { if (!seciliKategori && aktifKategoriler.length > 0) setSeciliKategori(aktifKategoriler[0].kategoriID); }, [aktifKategoriler, seciliKategori]);
  const filtrelenmis = seciliKategori ? urunler.filter(u => u.kategoriID === seciliKategori) : urunler;
  return (
    <div className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-surface-100 dark:border-surface-700">
        <h3 className="font-display font-bold text-surface-800 dark:text-white text-sm">Ürünler</h3>
        <button onClick={onAdd}
          className="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold rounded-xl shadow-lg shadow-emerald-500/20 transition-all">+ Yeni Ekle</button>
      </div>
      <div className="flex gap-1.5 p-3 border-b border-surface-100 dark:border-surface-700 overflow-x-auto">
        {aktifKategoriler.map(k => (
          <button key={k.kategoriID} onClick={() => setSeciliKategori(k.kategoriID)}
            className={`px-3 py-1.5 text-[10px] font-semibold rounded-xl whitespace-nowrap transition-all ${
              seciliKategori === k.kategoriID ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' : 'bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-600'
            }`}>
            {k.kategoriAdi} <span className="ml-1 text-[9px] opacity-60">({urunler.filter(u => u.kategoriID === k.kategoriID).length})</span>
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        {filtrelenmis.length === 0 ? (
          <div className="p-8 text-center text-xs text-surface-400">Bu kategoride ürün bulunamadı</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-surface-50 dark:bg-surface-700/30">
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Ürün Adı</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Bölüm</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Fiyat</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Stok</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Süre</th>
                <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtrelenmis.map(u => {
                const kat = kategoriler.find(k => k.kategoriID === u.kategoriID);
                return (
                  <tr key={u.urunID} className="border-t border-surface-100 dark:border-surface-700/50 hover:bg-surface-50 dark:hover:bg-surface-700/20 transition-colors">
                    <td className="px-4 py-2.5 text-surface-600 dark:text-surface-300 font-medium">{u.urunAdi}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${
                        kat?.departman === 'Bar' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                        kat?.departman === 'Mutfak' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                        'bg-surface-100 text-surface-500'
                      }`}>{kat?.departman || '-'}</span>
                    </td>
                    <td className="px-4 py-2.5 text-surface-600 dark:text-surface-300">{fm(u.fiyat)}</td>
                    <td className="px-4 py-2.5 text-surface-600 dark:text-surface-300">{u.stokMiktari}</td>
                    <td className="px-4 py-2.5 text-surface-600 dark:text-surface-300">{u.hazirlamaSuresi} dk</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => onEdit(u)}
                          className="px-2.5 py-1 text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">Düzenle</button>
                        <button onClick={() => onDel(u.urunID)}
                          className="px-2.5 py-1 text-[10px] font-semibold bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">Sil</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const MasalarTab = ({ masalar, onEdit, onAdd, onDel }) => {
  const konumSet = [...new Set(masalar.map(m => m.konum || 'Diğer'))].sort();
  const [seciliKonum, setSeciliKonum] = useState(null);
  const filtrelenmis = seciliKonum ? masalar.filter(m => (m.konum || 'Diğer') === seciliKonum) : masalar;
  return (
    <div className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-surface-100 dark:border-surface-700">
        <h3 className="font-display font-bold text-surface-800 dark:text-white text-sm">Masalar</h3>
        <button onClick={onAdd}
          className="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold rounded-xl shadow-lg shadow-emerald-500/20 transition-all">+ Yeni Masa</button>
      </div>
      <div className="flex gap-1.5 p-3 border-b border-surface-100 dark:border-surface-700 overflow-x-auto">
        <button onClick={() => setSeciliKonum(null)}
          className={`px-3 py-1.5 text-[10px] font-semibold rounded-xl whitespace-nowrap transition-all ${!seciliKonum ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' : 'bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-600'}`}>
          Tümü <span className="ml-1 text-[9px] opacity-60">({masalar.length})</span>
        </button>
        {konumSet.map(k => (
          <button key={k} onClick={() => setSeciliKonum(k)}
            className={`px-3 py-1.5 text-[10px] font-semibold rounded-xl whitespace-nowrap transition-all ${seciliKonum === k ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' : 'bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-600'}`}>
            {k} <span className="ml-1 text-[9px] opacity-60">({masalar.filter(m => (m.konum || 'Diğer') === k).length})</span>
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        {filtrelenmis.length === 0 ? (
          <div className="p-8 text-center text-xs text-surface-400">Henüz masa eklenmemiş</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-surface-50 dark:bg-surface-700/30">
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">No</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Konum</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Kapasite</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Durum</th>
                <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-surface-400 uppercase tracking-wider">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtrelenmis.map(m => (
                <tr key={m.masaID} className="border-t border-surface-100 dark:border-surface-700/50 hover:bg-surface-50 dark:hover:bg-surface-700/20 transition-colors">
                  <td className="px-4 py-2.5 text-surface-600 dark:text-surface-300 font-medium">{m.masaNumarasi}</td>
                  <td className="px-4 py-2.5 text-surface-600 dark:text-surface-300">{m.konum || '-'}</td>
                  <td className="px-4 py-2.5 text-surface-600 dark:text-surface-300">{m.kapasite}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${
                      m.durum === 'Dolu' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' :
                      m.durum === 'Rezerve' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                      'bg-surface-100 text-surface-500 dark:bg-surface-700 dark:text-surface-400'
                    }`}>{m.durum}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => onEdit(m)}
                        className="px-2.5 py-1 text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors">Düzenle</button>
                      <button onClick={() => onDel(m.masaID)}
                        className="px-2.5 py-1 text-[10px] font-semibold bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">Sil</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const Label = ({ children }) => <label className="flex flex-col gap-1 text-[11px] font-medium text-surface-400">{children}</label>;
const Input = (p) => <input {...p} className="w-full text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3.5 py-2.5 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />;
const Select = (p) => <select {...p} className="w-full text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3.5 py-2.5 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />;
const Textarea = (p) => <textarea {...p} className="w-full text-xs bg-surface-50 dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-xl px-3.5 py-2.5 text-surface-700 dark:text-surface-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none" />;

const FormModal = ({ modal, kategoriler, urunler, masalar, onSave, onClose }) => {
  const [form, setForm] = useState(modal.item || {});
  const fileRef = useRef(null);
  useEffect(() => { setForm(modal.item || {}); }, [modal]);
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => set('resimBase64', ev.target.result);
    reader.readAsDataURL(file);
  };
  const handleSubmit = (e) => { e.preventDefault(); onSave(modal.type, form); };
  const title = modal.item ? 'Düzenle' : 'Yeni Ekle';
  const typeLabel = { urun: 'Ürün', kategori: 'Kategori', masa: 'Masa', personel: 'Personel', iletisim: 'İletişim', stok: 'Stok Hareketi' }[modal.type];

  const konumlar = [...new Set((masalar || []).map(m => m.konum).filter(Boolean))].sort();

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-surface-800 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-soft-xl animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-surface-100 dark:border-surface-700">
          <h2 className="font-display font-bold text-surface-800 dark:text-white text-sm">{title} {typeLabel}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-700 text-surface-400 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {modal.type === 'kategori' && (
            <>
              <Label>Kategori Adı<Input value={form.kategoriAdi || ''} onChange={e => set('kategoriAdi', e.target.value)} required /></Label>
              <Label>Açıklama<Textarea rows={2} value={form.aciklama || ''} onChange={e => set('aciklama', e.target.value)} /></Label>
              <Label>Bölüm (Yazdırma Bölümü)<Select value={form.departman || 'MUTFAK'} onChange={e => set('departman', e.target.value.toUpperCase())}>
                {['MUTFAK', 'BAR', 'NARGİLE', 'KASA'].map(d => <option key={d} value={d}>{d}</option>)}
              </Select></Label>
              <Label>Resim<input type="file" accept="image/*" onChange={handleImage} ref={fileRef} className="text-xs text-surface-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-[11px] file:font-semibold file:bg-emerald-50 dark:file:bg-emerald-900/20 file:text-emerald-600 dark:file:text-emerald-400" /></Label>
              {form.resimBase64 && <img src={form.resimBase64} alt="preview" className="w-full max-h-32 object-contain rounded-xl bg-surface-50 dark:bg-surface-700" />}
            </>
          )}
          {modal.type === 'urun' && (
            <>
              <Label>Ürün Adı<Input value={form.urunAdi || ''} onChange={e => set('urunAdi', e.target.value)} required /></Label>
              <Label>Kategori<Select value={form.kategoriID || ''} onChange={e => set('kategoriID', parseInt(e.target.value))} required>
                <option value="">Seçiniz</option>
                {kategoriler.map(k => <option key={k.kategoriID} value={k.kategoriID}>{k.kategoriAdi}</option>)}
              </Select></Label>
              <Label>Fiyat<Input type="number" step="0.01" value={form.fiyat || ''} onChange={e => set('fiyat', parseFloat(e.target.value))} required /></Label>
              <Label>Stok<Input type="number" value={form.stokMiktari ?? ''} onChange={e => set('stokMiktari', parseInt(e.target.value) || 0)} /></Label>
              <Label>Hazırlama Süresi (dk)<Input type="number" value={form.hazirlamaSuresi || ''} onChange={e => set('hazirlamaSuresi', parseInt(e.target.value) || 0)} /></Label>
              <Label>Açıklama<Textarea rows={2} value={form.aciklama || ''} onChange={e => set('aciklama', e.target.value)} /></Label>
              <Label>Resim<input type="file" accept="image/*" onChange={handleImage} className="text-xs text-surface-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-[11px] file:font-semibold file:bg-emerald-50 dark:file:bg-emerald-900/20 file:text-emerald-600 dark:file:text-emerald-400" /></Label>
              {form.resimBase64 && <img src={form.resimBase64} alt="preview" className="w-full max-h-32 object-contain rounded-xl bg-surface-50 dark:bg-surface-700" />}
            </>
          )}
          {modal.type === 'masa' && (
            <>
              <Label>Masa Numarası<Input value={form.masaNumarasi || ''} onChange={e => set('masaNumarasi', e.target.value)} required /></Label>
              <Label>Konum<Input value={form.konum || ''} onChange={e => set('konum', e.target.value)} /></Label>
              <Label>Kapasite<Input type="number" value={form.kapasite || ''} onChange={e => set('kapasite', parseInt(e.target.value))} required /></Label>
            </>
          )}
          {modal.type === 'personel' && (
            <>
              <Label>Kullanıcı Adı<Input value={form.kullaniciAdi || ''} onChange={e => set('kullaniciAdi', e.target.value)} required disabled={!!modal.item} /></Label>
              <Label>Şifre{modal.item ? ' (boş bırakılırsa değişmez)' : ''}<Input type="password" value={form.sifre || ''} onChange={e => set('sifre', e.target.value)} required={!modal.item} /></Label>
              <Label>Ad Soyad<Input value={form.adSoyad || ''} onChange={e => set('adSoyad', e.target.value)} required /></Label>
              <Label>Rol<Select value={form.rol || 'Garson'} onChange={e => set('rol', e.target.value)}>
                {['Admin', 'Garson', 'Kasa', 'Mutfak'].map(r => <option key={r} value={r}>{r}</option>)}
              </Select></Label>
              <Label>Telefon<Input value={form.telefon || ''} onChange={e => set('telefon', e.target.value)} /></Label>
              <Label>E-posta<Input type="email" value={form.eposta || ''} onChange={e => set('eposta', e.target.value)} /></Label>
              <label className="flex items-center gap-2.5 text-[11px] font-medium text-surface-400">
                <input type="checkbox" checked={form.aktif !== false} onChange={e => set('aktif', e.target.checked)}
                  className="w-4 h-4 rounded border-surface-300 dark:border-surface-600 text-emerald-500 focus:ring-emerald-500/20" />
                Aktif
              </label>
              <Label>Profil Resmi<input type="file" accept="image/*" onChange={handleImage} className="text-xs text-surface-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-[11px] file:font-semibold file:bg-emerald-50 dark:file:bg-emerald-900/20 file:text-emerald-600 dark:file:text-emerald-400" /></Label>
              {form.resimBase64 && <img src={form.resimBase64} alt="preview" className="w-full max-h-32 object-contain rounded-xl bg-surface-50 dark:bg-surface-700" />}
            </>
          )}
          {modal.type === 'iletisim' && (
            <>
              <Label>Anahtar<Input value={form.anahtar || ''} onChange={e => set('anahtar', e.target.value)} required placeholder="ornek: telefon, adres, email" /></Label>
              <Label>Değer<Input value={form.deger || ''} onChange={e => set('deger', e.target.value)} required /></Label>
              <Label>Açıklama<Input value={form.aciklama || ''} onChange={e => set('aciklama', e.target.value)} /></Label>
            </>
          )}
          {modal.type === 'stok' && (
            <>
              <Label>Ürün<Select value={form.urunID || ''} onChange={e => set('urunID', parseInt(e.target.value))} required>
                <option value="">Seçiniz</option>
                {urunler.map(u => <option key={u.urunID} value={u.urunID}>{u.urunAdi} (Stok: {u.stokMiktari})</option>)}
              </Select></Label>
              <Label>Hareket Türü<Select value={form.hareketTuru || 'Giris'} onChange={e => set('hareketTuru', e.target.value)}>
                <option value="Giris">Giriş (+)</option>
                <option value="Cikis">Çıkış (-)</option>
              </Select></Label>
              <Label>Miktar<Input type="number" value={form.miktar || ''} onChange={e => set('miktar', parseInt(e.target.value))} required /></Label>
              <Label>Açıklama<Input value={form.aciklama || ''} onChange={e => set('aciklama', e.target.value)} /></Label>
            </>
          )}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 text-xs font-semibold bg-surface-100 dark:bg-surface-700 text-surface-500 dark:text-surface-400 rounded-xl hover:bg-surface-200 dark:hover:bg-surface-600 transition-all">
              İptal
            </button>
            <button type="submit"
              className="flex-1 py-2.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98]">
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SistemTab = () => {
  const { token, isSystemLocked, checkLockStatus } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [lockInfo, setLockInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);

  const fixStuckTables = async () => {
    setFixing(true);
    try {
      const res = await fetch(`${API}/masalar/sikismis-duzelt`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ onay: false })
      });
      const data = await res.json();
      if (!data.masalar || data.masalar.length === 0) {
        Swal.fire({ title: 'Sıkışmış masa bulunamadı', icon: 'success', timer: 2000, showConfirmButton: false });
        setFixing(false); return;
      }
      const liste = data.masalar.map((m, i) => `${i + 1}. ${m.masaNumarasi}`).join('\n');
      const result = await Swal.fire({
        title: `${data.masalar.length} masa sıkışmış`,
        html: `<div style="text-align:left;font-size:13px"><p style="margin-bottom:8px">Bunlar "Boş" yapılacak:</p><pre style="background:#f1f5f9;padding:10px;border-radius:8px;font-family:monospace">${liste}</pre></div>`,
        icon: 'question',
        showCancelButton: true, confirmButtonText: 'Evet, Düzelt', cancelButtonText: 'Vazgeç',
        confirmButtonColor: '#10b981'
      });
      if (!result.isConfirmed) { setFixing(false); return; }
      const res2 = await fetch(`${API}/masalar/sikismis-duzelt`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ onay: true })
      });
      const data2 = await res2.json();
      Swal.fire({ title: data2.mesaj, icon: 'success', timer: 3000, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Hata', 'İşlem sırasında hata oluştu!', 'error');
    }
    setFixing(false);
  };

  const toggleLock = async (force = false) => {
    setLoading(true);
    try {
      const endpoint = isSystemLocked ? 'unlock' : 'lock';
      const url = `${API}/sitesettings/${endpoint}${force ? '?force=true' : ''}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.requiresConfirmation) {
        setLockInfo(data);
        setConfirming(true);
      } else {
        await checkLockStatus();
        Swal.fire({ title: isSystemLocked ? 'Sistem Açıldı' : 'Sistem Kapatıldı', text: data.message, icon: 'success', timer: 2000, showConfirmButton: false });
      }
    } catch (e) {
      Swal.fire('Hata', 'İşlem sırasında hata oluştu!', 'error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-display font-bold text-surface-800 dark:text-white text-base">Sistem Durumu</h3>
              <p className="text-xs text-surface-400 mt-1">Tüm sistemin işlem durumunu kontrol edin</p>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${
              isSystemLocked ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400' : 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
            }`}>
              <span className={`w-2 h-2 rounded-full ${isSystemLocked ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`} />
              {isSystemLocked ? 'İşlemler Kapalı' : 'İşlemler Açık'}
            </div>
          </div>

          <div className={`rounded-2xl p-6 text-center transition-all duration-300 ${
            isSystemLocked ? 'bg-red-50 dark:bg-red-900/5 border-2 border-red-200 dark:border-red-800' : 'bg-emerald-50 dark:bg-emerald-900/5 border-2 border-emerald-200 dark:border-emerald-800'
          }`}>
            <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-3 transition-all ${
              isSystemLocked ? 'bg-red-100 dark:bg-red-900/20' : 'bg-emerald-100 dark:bg-emerald-900/20'
            }`}>
              {isSystemLocked ? (
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            <h4 className={`text-lg font-bold mb-1 ${isSystemLocked ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
              {isSystemLocked ? 'İşlemler Kapalı' : 'İşlemler Açık'}
            </h4>
            <p className="text-xs text-surface-500 dark:text-surface-400 mb-4">
              {isSystemLocked
                ? 'Garson, bar ve mutfak kullanıcıları işlem yapamaz. Sadece Admin ve Kasa rolündekiler erişebilir.'
                : 'Tüm kullanıcılar normal şekilde işlem yapabilir.'}
            </p>
            <button onClick={() => toggleLock()} disabled={loading}
              className={`px-8 py-3 text-sm font-bold rounded-xl transition-all active:scale-[0.97] disabled:opacity-50 ${
                isSystemLocked
                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25'
                  : 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/25'
              }`}>
              {loading ? 'İşleniyor...' : isSystemLocked ? 'İşlemleri Aç' : 'İşlemleri Kapat'}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft overflow-hidden">
        <div className="p-6">
          <h3 className="font-display font-bold text-surface-800 dark:text-white text-base mb-1">Sıkışmış Masalar</h3>
          <p className="text-xs text-surface-400 mb-4">Durumu "Dolu" ama aktif siparişi olmayan masaları düzeltir.</p>
          <button onClick={fixStuckTables} disabled={fixing}
            className="px-6 py-2.5 text-sm font-bold bg-amber-500 hover:bg-amber-600 text-white rounded-xl shadow-lg shadow-amber-500/25 transition-all active:scale-[0.97] disabled:opacity-50">
            {fixing ? 'Düzeltiliyor...' : 'Sıkışmış Masaları Düzelt'}
          </button>
        </div>
      </div>

      {confirming && lockInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setConfirming(false); setLockInfo(null); }}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative bg-white dark:bg-surface-800 rounded-3xl w-full max-w-md p-6 shadow-soft-xl animate-scale-in" onClick={e => e.stopPropagation()}>
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center">
              <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-surface-800 dark:text-white text-center mb-2">İşlemleri Kapat</h3>
            <p className="text-sm text-surface-500 dark:text-surface-400 text-center mb-4">{lockInfo.message}</p>
            <div className="flex gap-2">
              <button onClick={() => { setConfirming(false); setLockInfo(null); }}
                className="flex-1 py-2.5 text-xs font-semibold bg-gray-100 dark:bg-surface-700 text-gray-500 dark:text-surface-400 rounded-xl hover:bg-gray-200 dark:hover:bg-surface-600 transition-all">
                Vazgeç
              </button>
              <button onClick={() => { setConfirming(false); setLockInfo(null); toggleLock(true); }}
                className="flex-1 py-2.5 text-xs font-semibold bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg shadow-red-500/20 transition-all">
                Yine de Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TABS = [
  { key: 'siparisler', label: 'Siparişler', icon: '📋' },
  { key: 'raporlar', label: 'Raporlar', icon: '📈' },
  { key: 'ozet', label: 'Özet', icon: '📊' },
  { key: 'urunler', label: 'Ürünler', icon: '🍽️' },
  { key: 'kategoriler', label: 'Kategoriler', icon: '📂' },
  { key: 'masalar', label: 'Masalar', icon: '🪑' },
  { key: 'personel', label: 'Personel', icon: '👥' },
  { key: 'stok', label: 'Stok', icon: '📦' },
  { key: 'iletisim', label: 'İletişim', icon: '📞' },
  { key: 'adisyon', label: 'Fiş Tasarımı', icon: '🖨️' },
  { key: 'site', label: 'Site Yönetimi', icon: '⚙️' },
  { key: 'yazicilar', label: 'Yazıcı Ayarları', icon: '🖨️' },
  { key: 'sistem', label: 'Sistem', icon: '🔒' },
  { key: 'modifier', label: 'Modifier', icon: '✨' },
];

export default function AdminDashboard() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const h = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [tab, setTab] = useState('siparisler');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ kategoriler: [], urunler: [], masalar: [], personel: [], siparisler: [], stok: [], iletisim: [], ozet: null });
  const [modal, setModal] = useState(null);
  const navRef = useRef(null);

  useEffect(() => { fetchAll(); const iv = setInterval(() => { if (!modal) fetchAll(true); }, 30000); return () => clearInterval(iv); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl('/hubs/siparis')
      .configureLogging(LogLevel.Warning)
      .build();
    connection.on('YeniSiparisAlindi', () => fetchAll(true));
    connection.on('SiparisGuncellendi', () => fetchAll(true));
    connection.on('SiparisDurumuDegisti', () => fetchAll(true));
    connection.on('SiparisKapatildi', () => fetchAll(true));
    connection.start().catch(() => {});
    return () => { connection.stop(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const api = async (method, path, body) => {
    const opts = { method, headers: h };
    if (body) opts.body = JSON.stringify(body);
    const res = await fetch(API + path, opts);
    if (!res.ok) throw new Error(await res.text());
    return res.status === 204 ? null : await res.json();
  };

  const fetchJson = async (url, opts) => {
    try { const r = await fetch(url, opts); if (!r.ok) { console.warn(url, 'status', r.status); return null; } return await r.json(); }
    catch (e) { console.warn(url, 'error', e.message); return null; }
  };

  const fetchAll = async (silent) => {
    if (!silent) setLoading(true);
    const [k, u, m, p, s, st, i, o] = await Promise.all([
      fetchJson(`${API}/kategoriler`, { headers: h }), fetchJson(`${API}/urunler`, { headers: h }),
      fetchJson(`${API}/masalar`, { headers: h }), fetchJson(`${API}/kullanicilar`, { headers: h }),
      fetchJson(`${API}/siparisler`, { headers: h }), fetchJson(`${API}/stok`, { headers: h }),
      fetchJson(`${API}/iletisim`, { headers: h }), fetchJson(`${API}/raporlar/gunluk-ozet`, { headers: h })
    ]);
    const masalarData = Array.isArray(m) ? m : [];
    masalarData.sort((a, b) => (parseInt((a.masaNumarasi || '').replace(/\D/g, ''), 10) || 0) - (parseInt((b.masaNumarasi || '').replace(/\D/g, ''), 10) || 0));
    setData({ kategoriler: Array.isArray(k) ? k : [], urunler: Array.isArray(u) ? u : [], masalar: masalarData, personel: Array.isArray(p) ? p : [], siparisler: Array.isArray(s) ? s : [], stok: Array.isArray(st) ? st : [], iletisim: Array.isArray(i) ? i : [], ozet: o });
    if (!silent) setLoading(false);
  };

  const save = async (type, item) => {
    try {
      const paths = { kategori: '/kategoriler', urun: '/urunler', masa: '/masalar', personel: '/kullanicilar', iletisim: '/iletisim', stok: '/stok' };
      const idKey = { kategori: 'kategoriID', urun: 'urunID', masa: 'masaID', personel: 'kullaniciID', iletisim: 'id' }[type];
      if (item[idKey]) await api('PUT', `${paths[type]}/${item[idKey]}`, item);
      else await api('POST', paths[type], item);
      setModal(null); await fetchAll(true);
      Swal.fire('Başarılı', 'Kayıt işlemi tamamlandı.', 'success');
    } catch (e) { Swal.fire('Hata', e.message, 'error'); }
  };

  const del = async (type, id) => {
    const res = await Swal.fire({ title: 'Emin misiniz?', text: 'Bu kaydı silmek istediğinize emin misiniz?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Evet, Sil!', cancelButtonText: 'İptal', confirmButtonColor: '#ef4444' });
    if (!res.isConfirmed) return;
    try {
      const paths = { kategori: '/kategoriler/', urun: '/urunler/', masa: '/masalar/', personel: '/kullanicilar/', iletisim: '/iletisim/' };
      await api('DELETE', `${paths[type]}${id}`);
      await fetchAll(true);
      Swal.fire('Silindi!', 'Kayıt başarıyla silindi.', 'success');
    } catch (e) { Swal.fire('Hata', e.message, 'error'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-surface-400 font-medium">Yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-surface-800/80 backdrop-blur-xl border-b border-surface-100 dark:border-surface-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h1 className="font-display font-bold text-surface-800 dark:text-white text-sm">Admin Paneli</h1>
            </div>
            <button onClick={() => { logout(); navigate('/'); }}
              className="px-3.5 py-1.5 text-[11px] font-medium text-red-500 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-all">
              Çıkış
            </button>
          </div>
        </div>
      </header>

      {/* Nav tabs */}
      <div className="sticky top-14 z-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div ref={navRef} className="admin-nav-glass flex flex-nowrap gap-1 overflow-x-auto px-2 py-1.5">
            {TABS.map(t => (
              <TabButton key={t.key} active={tab === t.key} icon={t.icon} label={t.label}
                onClick={() => setTab(t.key)} />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {tab === 'ozet' && <DashboardMain />}
        {tab === 'urunler' && <div className="animate-fade-in"><UrunlerTab kategoriler={data.kategoriler} urunler={data.urunler} onEdit={item => setModal({ type: 'urun', item })} onAdd={() => setModal({ type: 'urun', item: null })} onDel={id => del('urun', id)} /></div>}
        {tab === 'kategoriler' && <div className="animate-fade-in"><CrudTable type="kategoriler" list={data.kategoriler} cols={['kategoriAdi','departman','aciklama']} labels={['Ad','Bölüm','Açıklama']} idKey="kategoriID" onEdit={item => setModal({ type: 'kategori', item })} onAdd={() => setModal({ type: 'kategori', item: null })} onDel={id => del('kategori', id)} /></div>}
        {tab === 'masalar' && <div className="animate-fade-in"><MasalarTab masalar={data.masalar} onEdit={item => setModal({ type: 'masa', item })} onAdd={() => setModal({ type: 'masa', item: null })} onDel={id => del('masa', id)} /></div>}
        {tab === 'personel' && <div className="animate-fade-in"><CrudTable type="personel" list={data.personel} cols={['kullaniciAdi','adSoyad','rol','aktif','telefon','eposta']} labels={['K.Adı','Ad Soyad','Rol','Durum','Tel','E-posta']} idKey="kullaniciID" onEdit={item => setModal({ type: 'personel', item })} onAdd={() => setModal({ type: 'personel', item: null })} onDel={id => del('personel', id)} /></div>}
        {tab === 'siparisler' && <div className="animate-fade-in"><SiparisTab /></div>}
        {tab === 'raporlar' && <div className="animate-fade-in"><ReportsDashboard /></div>}
        {tab === 'stok' && <div className="animate-fade-in"><StokTab hareketler={data.stok} urunler={data.urunler} onAdd={() => setModal({ type: 'stok', item: null })} /></div>}
        {tab === 'iletisim' && <div className="animate-fade-in"><CrudTable type="iletişim" list={data.iletisim} cols={['anahtar','deger','aciklama']} labels={['Anahtar','Değer','Açıklama']} idKey="id" onEdit={item => setModal({ type: 'iletisim', item })} onAdd={() => setModal({ type: 'iletisim', item: null })} onDel={id => del('iletisim', id)} /></div>}
        {tab === 'adisyon' && <div className="animate-fade-in"><AdisyonTab /></div>}
        {tab === 'site' && <div className="animate-fade-in"><SiteSettings /></div>}
        {tab === 'yazicilar' && <div className="animate-fade-in"><YaziciYonetimi /></div>}
        {tab === 'sistem' && <div className="animate-fade-in"><SistemTab /></div>}
        {tab === 'modifier' && <div className="animate-fade-in"><ModifierYonetimi kategoriler={data.kategoriler} urunler={data.urunler} onUrunGuncelle={fetchAll} /></div>}
        {modal && <FormModal modal={modal} kategoriler={data.kategoriler} urunler={data.urunler} masalar={data.masalar} onSave={save} onClose={() => setModal(null)} />}
      </div>
    </div>
  );
}
